import React, { type ReactNode } from 'react';

import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';

import { type ProductConfigs, useProductConfigs } from '../../../common/constants/products';
import {
	type PreparedResultAttributes,
	usePreparedResultAnalytics,
} from '../../../controllers/analytics/prepared-result-attributes';
import { useCommentsFilterActive } from '../../../controllers/filters/selected/use-comments-filter-active';

export const RESULT_DERIVED_DATA_EXPERIMENT = 'rovo_fps_share_result_derived_data';

/** Undefined preserves standalone and experiment-control consumers. */
export type ResultDerivedDataProps = {
	preparedResultAttributes?: PreparedResultAttributes;
	isCommentsFilterActive?: boolean;
	productConfigs?: ProductConfigs;
};

type Props = {
	children: (data: ResultDerivedDataProps) => ReactNode;
};

/** Prepare list-wide values once, then expose each dependency as an explicit result prop. */
export const ResultDerivedData = ({ children }: Props): React.JSX.Element =>
	expVal(RESULT_DERIVED_DATA_EXPERIMENT, 'isEnabled', false) ? (
		<PreparedResultPropsBoundary>{children}</PreparedResultPropsBoundary>
	) : (
		<>{children({})}</>
	);

/**
 * Add state here only when the value is stable and identical across the list, multiple rows need
 * it, and a change should rerender the whole list. Keep row-specific, interaction-time and
 * frequently changing state at the row or callback boundary.
 */
const PreparedResultPropsBoundary = ({ children }: Props): React.JSX.Element => {
	const preparedResultAttributes = usePreparedResultAnalytics();
	const isCommentsFilterActive = useCommentsFilterActive();
	const productConfigs = useProductConfigs();
	return (
		<>
			{children({
				preparedResultAttributes,
				isCommentsFilterActive,
				productConfigs,
			})}
		</>
	);
};
